import { useState, useEffect, useRef, useCallback } from 'react';
import { staffAuthApi } from '../lib/api';
import { parseHash, startPageUrl } from '../lib/sites';
import {
  STAFF_SIGNED_OUT, staffTokenKey, getStaffToken, setStaffToken, clearStaffToken,
  signInUrlFor, forgetBounces, takeHandoffCode,
} from '../lib/staffSession';

// A code being redeemed, per site: a remount mid-redeem waits for it instead
// of finding no token and leaving for the start page again.
const redeeming = {};

// One staff site's sign-in (lib/staffSession.js). With none, the browser goes
// to the start page and comes back signed in; `token` is '' until then, and
// `problem` says why it stopped instead. `fresh` is true when the sign-in
// arrived during this visit.
export default function useStaffSession(site) {
  const [token, setToken] = useState(() => (parseHash().params.has('handoff') ? '' : getStaffToken(site)));
  const [problem, setProblem] = useState('');
  const [fresh, setFresh] = useState(false);
  const leaving = useRef(false);

  const goSignIn = useCallback(() => {
    if (leaving.current) return;
    const url = signInUrlFor(site);
    if (!url) {
      setProblem("We couldn't sign you in automatically.");
      return;
    }
    leaving.current = true;
    window.location.replace(url);
  }, [site]);

  useEffect(() => {
    let cancelled = false;
    const code = takeHandoffCode(site);
    if (code) {
      redeeming[site] = staffAuthApi.redeem(code, site)
        .then(({ token: t }) => { setStaffToken(site, t); forgetBounces(site); return t; })
        .finally(() => { delete redeeming[site]; });
    }
    if (redeeming[site]) {
      redeeming[site]
        .then((t) => { if (!cancelled) { setToken(t); setFresh(true); } })
        .catch((e) => { if (!cancelled) setProblem(e.message || 'Sign-in failed.'); });
    } else if (getStaffToken(site)) {
      setToken(getStaffToken(site));
    } else {
      goSignIn();
    }
    return () => { cancelled = true; };
  }, [site, goSignIn]);

  useEffect(() => {
    const key = staffTokenKey(site);
    const onSignedOut = (e) => {
      if (e.detail?.key !== key) return;
      setToken('');
      goSignIn();
    };
    // This site, open in another tab, signed in or out.
    const onStorage = (e) => {
      if (e.key !== key) return;
      if (e.newValue) { setToken(e.newValue); return; }
      setToken('');
      goSignIn();
    };
    window.addEventListener(STAFF_SIGNED_OUT, onSignedOut);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(STAFF_SIGNED_OUT, onSignedOut);
      window.removeEventListener('storage', onStorage);
    };
  }, [site, goSignIn]);

  const retry = useCallback(() => {
    forgetBounces(site);
    setProblem('');
    leaving.current = false;
    goSignIn();
  }, [site, goSignIn]);

  // Signs this browser out of every staff site, then shows the start page.
  const signOut = useCallback(async () => {
    leaving.current = true;
    const t = getStaffToken(site);
    clearStaffToken(site);
    if (t) await staffAuthApi.logout(t).catch(() => {});
    window.location.replace(startPageUrl({ signedout: '1' }));
  }, [site]);

  return { token, problem, fresh, retry, signOut };
}
